/**
 * Player profiles on this device (development and QA, from the debug page): each profile is a
 * whole game state, i.e. every game key in localStorage (progress, age, intro, story beats). The
 * active profile lives in the ordinary keys, so the game's stores need no knowledge of profiles;
 * the others are kept as snapshots. Switching saves the live keys into the active profile's
 * snapshot, restores the target's, and reloads the page (the stores load once at start).
 *
 * Kept apart from game state: the profile registry and snapshots themselves, the debug switch,
 * the QA recorder's data, and session-only keys.
 */
const PREFIX = 'foe-names.';
const REGISTRY = 'foe-names.profiles.v1';
const SNAPSHOT = 'foe-names.profile.';
const NOT_GAME = [
  REGISTRY,
  SNAPSHOT,
  'foe-names.debug',
  'foe-names.audiolab',
  'foe-names.qa',
  'foe-names.reloaded-for',
];

export interface Profile {
  id: string;
  name: string;
  createdAt: number;
  /** When it was last active (switched away from or created). */
  usedAt: number;
}

interface Registry {
  active: string;
  profiles: Profile[];
}

/** A game state: game key to stored string. */
export type Snapshot = Record<string, string>;

/** Whether `key` is part of a game state (cleared by "Start over", saved in a profile). */
export function isGameKey(key: string): boolean {
  return key.startsWith(PREFIX) && !NOT_GAME.some((k) => key.startsWith(k));
}

function storage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

function keys(s: Storage): string[] {
  const out: string[] = [];
  for (let i = 0; i < s.length; i++) {
    const key = s.key(i);
    if (key !== null) out.push(key);
  }
  return out;
}

/** The live game state (the active profile's). */
export function liveSnapshot(): Snapshot {
  const s = storage();
  if (!s) return {};
  const out: Snapshot = {};
  for (const key of keys(s)) if (isGameKey(key)) out[key] = s.getItem(key) ?? '';
  return out;
}

function clearLive(s: Storage) {
  for (const key of keys(s)) if (isGameKey(key)) s.removeItem(key);
}

function writeLive(s: Storage, snapshot: Snapshot) {
  clearLive(s);
  for (const [key, value] of Object.entries(snapshot)) if (isGameKey(key)) s.setItem(key, value);
}

const newId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** The registry, created on first use with one profile holding the current game state. */
function registry(s: Storage, now = Date.now()): Registry {
  try {
    const raw = s.getItem(REGISTRY);
    if (raw) {
      const r = JSON.parse(raw) as Registry;
      if (r.profiles.some((p) => p.id === r.active)) return r;
    }
  } catch {
    // unreadable: start a fresh registry around the live state
  }
  const first: Profile = { id: newId(), name: 'Player 1', createdAt: now, usedAt: now };
  const r = { active: first.id, profiles: [first] };
  save(s, r);
  return r;
}

function save(s: Storage, r: Registry) {
  s.setItem(REGISTRY, JSON.stringify(r));
}

export function snapshotOf(id: string): Snapshot {
  const s = storage();
  if (!s) return {};
  if (registry(s).active === id) return liveSnapshot();
  try {
    return JSON.parse(s.getItem(SNAPSHOT + id) ?? '{}') as Snapshot;
  } catch {
    return {};
  }
}

export interface ProfileList {
  active: string;
  profiles: readonly Profile[];
}

export function listProfiles(): ProfileList {
  const s = storage();
  if (!s) return { active: '', profiles: [] };
  const r = registry(s);
  return { active: r.active, profiles: r.profiles };
}

/** Saves the live state into the active profile and makes `id` live (the caller reloads). */
export function switchProfile(id: string, now = Date.now()): void {
  const s = storage();
  if (!s) return;
  const r = registry(s, now);
  if (r.active === id || !r.profiles.some((p) => p.id === id)) return;
  s.setItem(SNAPSHOT + r.active, JSON.stringify(liveSnapshot()));
  writeLive(s, snapshotOf(id));
  s.removeItem(SNAPSHOT + id);
  save(s, {
    active: id,
    profiles: r.profiles.map((p) => (p.id === r.active || p.id === id ? { ...p, usedAt: now } : p)),
  });
}

/**
 * Starts a new profile and makes it live (the caller reloads): empty (a new player, who sees the
 * intro and is asked their age), or a copy of the active one (`copy`).
 */
export function createProfile(name: string, copy = false, now = Date.now()): Profile | undefined {
  const s = storage();
  if (!s) return undefined;
  const r = registry(s, now);
  const live = liveSnapshot();
  s.setItem(SNAPSHOT + r.active, JSON.stringify(live));
  writeLive(s, copy ? live : {});
  const profile: Profile = { id: newId(), name: uniqueName(r, name), createdAt: now, usedAt: now };
  save(s, {
    active: profile.id,
    profiles: [...r.profiles.map((p) => (p.id === r.active ? { ...p, usedAt: now } : p)), profile],
  });
  return profile;
}

function uniqueName(r: Registry, name: string): string {
  const base = name.trim() || `Player ${String(r.profiles.length + 1)}`;
  const taken = new Set(r.profiles.map((p) => p.name));
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base} (${String(i)})`)) i++;
  return `${base} (${String(i)})`;
}

export function renameProfile(id: string, name: string): void {
  const s = storage();
  const trimmed = name.trim();
  if (!s || !trimmed) return;
  const r = registry(s);
  save(s, { ...r, profiles: r.profiles.map((p) => (p.id === id ? { ...p, name: trimmed } : p)) });
}

/**
 * Deletes a profile and its game state. Deleting the active one makes the most recently used other
 * profile live, or a new empty one when it was the last (the caller reloads then: `true`).
 */
export function deleteProfile(id: string, now = Date.now()): boolean {
  const s = storage();
  if (!s) return false;
  const r = registry(s, now);
  if (!r.profiles.some((p) => p.id === id)) return false;
  if (r.active !== id) {
    s.removeItem(SNAPSHOT + id);
    save(s, { ...r, profiles: r.profiles.filter((p) => p.id !== id) });
    return false;
  }
  const others = r.profiles.filter((p) => p.id !== id).sort((a, b) => b.usedAt - a.usedAt);
  const next = others[0];
  if (next) {
    writeLive(s, snapshotOf(next.id));
    s.removeItem(SNAPSHOT + next.id);
    save(s, {
      active: next.id,
      profiles: others.map((p) => (p === next ? { ...p, usedAt: now } : p)),
    });
  } else {
    clearLive(s);
    const fresh: Profile = { id: newId(), name: 'Player 1', createdAt: now, usedAt: now };
    save(s, { active: fresh.id, profiles: [fresh] });
  }
  return true;
}
